import { RecordingErrorType } from '../../serviceworkerBridgeClients/IScreenRecordingBridgeClient';

/**
 * InstructionsController class handles the initialization and setup of instructions for a task.
 * It manages various services and dependencies to ensure the correct flow and display of instructions.
 */
export class InstructionsController {
    /**
     * Constructs an instance of InstructionsController.
     * 
     * @param {Object} $scope - AngularJS scope object.
     * @param {Object} $rootScope - AngularJS root scope object.
     * @param {Object} $location - AngularJS $location service.
     * @param {Object} $injector - AngularJS $injector service.
     * @param {Object} $routeParams - AngularJS $routeParams service.
     * @param {Object} UserInfoService - Service to fetch user information.
     * @param {Object} TaskSelectionService - Service to handle task selection.
     * @param {Object} TaskFlowService - Service to manage task flow.
     * @param {Object} TaskInfoService - Service to fetch task information.
     * @param {Object} TaskDataService - Service to fetch task data.
     * @param {Object} $bridges - Service to handle bridge operations.
     * @param {Object} IAAPreferencesService - Service to fetch IAA preferences.
     * @param {Object} $translate - AngularJS $translate service.
     * @param {Object} TaskCountdownService - Service to manage task countdown.
     * @param {Object} InactivityTimeoutService - Service to handle inactivity timeouts.
     * @param {Object} ScreenRecordingBridgeClient - Service to handle screen recording.
     * @param {Object} $timeout - AngularJS $timeout service.
     */
    constructor($scope, $rootScope, $location, $injector, $routeParams, UserInfoService, TaskSelectionService, TaskFlowService, TaskInfoService, TaskDataService, $bridges, IAAPreferencesService, $translate, TaskCountdownService, InactivityTimeoutService, ScreenRecordingBridgeClient, $timeout) {
        this.$scope = $scope;
        this.$rootScope = $rootScope;
        this.$location = $location;
        this.$injector = $injector;
        this.$routeParams = $routeParams;
        this.UserInfoService = UserInfoService;
        this.TaskSelectionService = TaskSelectionService;
        this.TaskFlowService = TaskFlowService;
        this.TaskInfoService = TaskInfoService;
        this.TaskDataService = TaskDataService;
        this.$bridges = $bridges;
        this.IAAPreferencesService = IAAPreferencesService;
        this.$translate = $translate;
        this.TaskCountdownService = TaskCountdownService;
        this.InactivityTimeoutService = InactivityTimeoutService;
        this.ScreenRecordingBridgeClient = ScreenRecordingBridgeClient;
        this.$timeout = $timeout;

        // Ensure that all necessary redirects for task flow are registered
        this.TaskFlowService.initRedirects(this.$scope, this.$location);

        // Controller meta setup
        this.$logger = this.$bridges.logger();
        this.$auth = this.$bridges.authentication();
        this.$eventLog = this.$bridges.eventLog();

        this._BridgeRuntimeService = null;
        this.$scope._logIdentifier = 'InstructionsController';
        this.$logger.logEnter(this.$scope, 'initialisation');

        this.$scope.isCMS = this.isCMS.bind(this);

        // Private variables
        this.taskContext = this.TaskDataService.fetchContext();
        this.taskInfo = this.TaskInfoService.getTaskInfo();
        this.ISOLanguage = this.taskInfo.getISOLanguageCode();
        this.taskCountdownService = this.TaskCountdownService.getInstance();

        // Show Quit Button
        const today = new Date();
        const showQuitBtnDate = new Date(2017, 11, 1);
        this.$scope.showQuitBtn = showQuitBtnDate <= today;

        this.$scope.isMediaAssessment = false;
        if (this.taskInfo.isMediaAssessment()) {
            this.$scope.isMediaAssessment = true;
        }

        this.$scope.markLabel = this.taskInfo.getCustomMarkLabel() || this.$translate.instant("CONTENT.MARK", { totalMarks: 0 });
        this.$scope.marksLabel = this.taskInfo.getCustomMarksLabel() || this.$translate.instant("CONTENT.MARK", { totalMarks: 2 });

        // Setting basic template in case of a new Introduction Page
        let instructionsPageTemplate = "";

        /**
         * Fetches the instructions page object based on the task context and language code.
         * 
         * @returns {Object} - The instructions page object.
         */
        this.getInstructionsPageObject = function () {
            const _entries = this.taskContext.findInstancesByType('instructionsPage');
            if (angular.isObjectEmpty(_entries)) {
                return {}
            }
            let _result = {}
            angular.forEach(_entries, (entry, key) => {
                if (entry.getLanguageCode() === this.taskInfo.getLanguageCode()) {
                    _result = entry;
                }
            });

            return _result;
        };

        try {
            instructionsPageTemplate = window.languages[this.taskInfo.getISOLanguageCode()]['MODULES']['INSTRUCTIONS']['CONTENT'];
        } catch (e) {
            instructionsPageTemplate = window.languages['en_US']['MODULES']['INSTRUCTIONS']['CONTENT'];
        }

        const _instructionsPageObject = this.getInstructionsPageObject();

        if (!angular.isObjectEmpty(_instructionsPageObject)) {
            instructionsPageTemplate = _instructionsPageObject.data.content
        }
        this.$scope.instructionsPageContent = instructionsPageTemplate;

        // Assessment's mode. (aka !isCMS)

        const candidateUUID = this.$auth.getUserUUID();
        const candidate = this.UserInfoService.getUserInfo(candidateUUID);

        let taskUUID;
        if (candidate.getTaskUUID() === "undefined") {
            taskUUID = this.taskInfo.data.tasks[0].uuid;
            console.log("taskUUID", taskUUID);
        } else {
            taskUUID = candidate.getTaskUUID();
        }

        const task = this.taskContext.read(taskUUID);
        const sheets = task.getSheets();
        let showErrors;

        this.$scope.userInfo = candidate;
        // Private scope variables
        this.$scope.overviewUrl = '/overview/' + this.$scope.userInfo.getTaskUUID();
        this.$scope.IAAUrl = '/invigilatorMenu/' + this.$scope.userInfo.getTaskUUID();
        this.$scope.candidateIAAPreferences = null;

        // Show formatted time
        this.$scope.taskTimeFormatted = this.$scope.userInfo.getTaskTimeFormatted();
        // $scope.taskTimeFormattedMinusTenMinutes = $scope.userInfo.getTaskTimeFormatted($scope.userInfo.getTaskTime()-1000*60*10);

        // Add up total marks
        this.$scope.totalMarks = task.getTotalMarks();

        // Add number of sheets
        this.$scope.numSheets = Object.keys(task.getSheetsWithoutPreReleaseAndJournal()).length;

        this.$scope.candidateIAAPreferences = angular.copy(this.IAAPreferencesService.getPreferences());

        let existingExtraTimeMs = 0;
        let alreadyUsedTimeMS = 0;
        const hasExistingData = this.TaskFlowService.hasExistingDataLastResult();
        if (hasExistingData && (this.$bridges.data().get('timeElapsed') || this.$bridges.data().get("extraTimeTotal"))) {
            alreadyUsedTimeMS = this.$bridges.data().get('timeElapsed');
            if (!angular.isDefined(alreadyUsedTimeMS)) alreadyUsedTimeMS = 0;
            alreadyUsedTimeMS = parseInt(alreadyUsedTimeMS);
            this.taskCountdownService.setElapsedMillis(alreadyUsedTimeMS)

            let existingExtraTimeMsStr = this.$bridges.data().get("extraTimeTotal");
            if (!angular.isDefined(existingExtraTimeMsStr) || !existingExtraTimeMsStr) existingExtraTimeMsStr = 0;
            existingExtraTimeMs = parseInt(existingExtraTimeMsStr);

        }

        if (this.$scope.candidateIAAPreferences !== "" &&
            this.$scope.candidateIAAPreferences !== null &&
            typeof this.$scope.candidateIAAPreferences === "object" &&
            Object.keys(this.$scope.candidateIAAPreferences).length > 0) {

            delete this.$scope.candidateIAAPreferences[''];

            // Whitelist
            Object.keys(this.$scope.candidateIAAPreferences).
                map((item) => {
                    if (['colorTheme', 'extraTime', 'zoomLevel', 'candidateCanPause', 'videoSubtitleAutoEnable', 'dyslexicFont', 'alternativeContent'].indexOf(item) == -1) {
                        delete this.$scope.candidateIAAPreferences[item];
                    }
                });

            if (this.taskCountdownService.isExtraTimeChanged()) {
                this.$scope.candidateIAAPreferences['extraTime'] = this.taskCountdownService.getTotalExtraTimeMS() / 60000;
            }


            if (!angular.isDefined(this.$scope.candidateIAAPreferences['extraTime'])) this.$scope.candidateIAAPreferences['extraTime'] = 0;
            if (!angular.isDefined(existingExtraTimeMs)) {
                existingExtraTimeMs = this.$scope.candidateIAAPreferences['extraTime'] * 60000;
            } else {
                this.$scope.candidateIAAPreferences['extraTime'] = existingExtraTimeMs / 60000;
            }

        }

        if (!angular.isDefined(existingExtraTimeMs)) existingExtraTimeMs = 0;

        if (this.taskCountdownService.isExtraTimeChanged() || !this.$scope.candidateIAAPreferences['extraTime']) {
            existingExtraTimeMs = this.taskCountdownService.getTotalExtraTimeMS();
            if (existingExtraTimeMs === 0) {
                delete this.$scope.candidateIAAPreferences['extraTime'];
            } else {
                this.$scope.candidateIAAPreferences['extraTime'] = this.taskCountdownService.getTotalExtraTimeMS() / 60000;
            }
        }

        this.$scope.taskTimeFormatted = this.$scope.userInfo.getTaskTimeFormatted(this.$scope.userInfo.getTaskTime() - alreadyUsedTimeMS + existingExtraTimeMs);

        const _taskTime = this.$scope.userInfo.getTaskTime() - alreadyUsedTimeMS + existingExtraTimeMs;

        if (angular.isDefined(_taskTime)) {
            this.$scope.taskExpired = _taskTime <= 0;
        }

        this.$scope.onQuit = this.onQuit.bind(this);
        this.$scope.onStartButtonClick = this.onStartButtonClick.bind(this);
        this.$scope.onIAASetupButtonClick = this.onIAASetupButtonClick.bind(this);
        this.$scope.getTypeof = this.getTypeof.bind(this);
        this.$scope.retryScreenRecording = this.retryScreenRecording.bind(this);

        // Screen recording state
        this.$scope.screenRecordingStatus = {
            isStarting: false,
            hasError: false,
            errorMessage: '',
            errorType: null,
            retryCount: 0,
            maxRetries: 3
        };

        // Make enum available to template
        this.$scope.RecordingErrorType = RecordingErrorType;

        if (this.$scope.isMediaAssessment) {
            this.$location.path(this.$scope.overviewUrl);
        }

        /**
         * Checks if the provided color theme name is valid.
         * 
         * @param {string} name - The name of the color theme.
         * @returns {boolean} - True if the color theme is valid, false otherwise.
         */
        const isValidColorTheme = (name) => {

            if (window.languages) {
                const lang = window.languages[this.taskInfo.getISOLanguageCode()];
                const themes = lang['COLOR-THEME'];

                return Object.keys(themes).indexOf(name) != -1 ? true : false;
            }
            return false;
        }

        if (angular.isDefined(this.$rootScope.currentColorThemeObj) && angular.isDefined(this.$rootScope.currentColorThemeObj.name)) {
            console.info('Setting up color theme from scope:', this.$rootScope.currentColorThemeObj.name);
            this.$scope.currentColorThemeName = this.$translate.instant(this.$rootScope.currentColorThemeObj.name);

        } else if (angular.isDefined(this.$scope.candidateIAAPreferences) && angular.isDefined(this.$scope.candidateIAAPreferences['colorTheme'])) {

            if (isValidColorTheme(this.$scope.candidateIAAPreferences['colorTheme'])) {
                console.info('Setting up color theme from IAA Prefs:', this.$scope.candidateIAAPreferences['colorTheme']);
                this.$scope.currentColorThemeName = this.$translate.instant('COLOR-THEME.' + this.$scope.candidateIAAPreferences['colorTheme'].toUpperCase());
            } else {
                console.info('Setting up default color theme');
                this.$scope.currentColorThemeName = this.$translate.instant('COLOR-THEME.DEFAULT');
            }

        } else {
            console.info('Setting up default color theme');
            this.$scope.currentColorThemeName = this.$translate.instant('COLOR-THEME.DEFAULT');
        }

        // End is not CMS

        // Done
        this.$logger.logExit(this.$scope, 'initialisation');
    }

    /**
     * Checks if the current mode is CMS.
     * 
     * @returns {boolean} - True if the mode is CMS, false otherwise.
     */
    isCMS() {
        // Existing code here
    }

    /**
     * Handles the quit action.
     */
    onQuit() {
        this.BridgeRuntimeService().quit(false);
    }

    /**
     * Handles the start button click action.
     */
    async onStartButtonClick() {
        if (this.$scope.screenRecordingStatus.isStarting) {
            console.log("Screen recording is already being started, ignoring click");
            return;
        }

        // Immediately set the starting flag to prevent rapid clicks
        this.$scope.screenRecordingStatus.isStarting = true;
        this.$scope.screenRecordingStatus.hasError = false;
        this.$scope.screenRecordingStatus.errorMessage = '';

        try {
            await this.startScreenRecordingAndProceed();
        } catch (error) {
            console.warn("Error in onStartButtonClick:", error);
            if (window.Sentry) { window.Sentry.captureException(error, { tags: { context: 'screen_recording_start' } }); }
            this.$timeout(() => {
                this.$scope.screenRecordingStatus.isStarting = false;
                this.$scope.screenRecordingStatus.hasError = true;
                this.$scope.screenRecordingStatus.errorMessage = this.$translate.instant('MODULES.INSTRUCTIONS.SCREEN_RECORDING_UNKNOWN_ERROR');
            });
        }
    }

    /**
     * Attempts to start screen recording and proceed to the assessment.
     */
    async startScreenRecordingAndProceed() {
        // Note: isStarting flag is already set in onStartButtonClick to prevent rapid clicks

        // In dev mode, skip screen recording (not supported in headless/non-kiosk Chromium)
        // and proceed directly to the assessment.
        // @ts-ignore - __BUILD_NO__ is defined by webpack DefinePlugin
        if (typeof __BUILD_NO__ !== 'undefined' && __BUILD_NO__ === 'dev-local') {
            console.log("Dev mode: skipping screen recording, proceeding directly to assessment");
            this.proceedToAssessment();
            return;
        }

        // Get the candidate UUID from authentication service
        const candidateUUID = this.$auth.getUserUUID();
        
        // Get the task UUID - either from user info or task info
        let taskUUID;
        if (this.$scope.userInfo.getTaskUUID() === "undefined") {
            // Fallback to the first task in the task info if needed
            taskUUID = this.taskInfo.data.tasks[0].uuid;
        } else {
            taskUUID = this.$scope.userInfo.getTaskUUID();
        }
        
        console.log("Starting screen recording with taskUUID:", taskUUID, "candidateUUID:", candidateUUID);
        
        try {
            const result = await this.ScreenRecordingBridgeClient.startRecording(taskUUID, candidateUUID);
            
            if (result.success) {
                console.log("Screen recording started successfully, proceeding to assessment");
                this.proceedToAssessment();
            } else {
                console.warn("Screen recording failed:", result.error);
                this.reportScreenRecordingFailure(result.error);
                await this.handleScreenRecordingError(result.error);
            }
        } catch (error) {
            console.warn("Exception during screen recording start:", error);
            if (window.Sentry) { window.Sentry.captureException(error, { tags: { context: 'screen_recording_start' } }); }
            await this.handleScreenRecordingError({
                type: RecordingErrorType.UNKNOWN,
                message: 'Failed to start screen recording: ' + error.message
            });
        }
    }

    // PERMISSION_DENIED is expected UX (user canceled the share prompt); all other failures are bugs worth a warning.
    reportScreenRecordingFailure(error) {
        if (!window.Sentry) return;

        const retryCount = (this.$scope.screenRecordingStatus && this.$scope.screenRecordingStatus.retryCount) || 0;

        if (error.type === RecordingErrorType.PERMISSION_DENIED) {
            window.Sentry.captureMessage('Screen recording permission denied', {
                level: 'info',
                tags: { context: 'screen_recording_start', error_type: error.type, retry_count: retryCount },
            });
            return;
        }

        window.Sentry.captureMessage('Screen recording failed', {
            level: 'warning',
            tags: { context: 'screen_recording_start', error_type: error.type, retry_count: retryCount },
            extra: { error },
        });
    }

    /**
     * Handles screen recording errors and provides retry options.
     */
    async handleScreenRecordingError(error) {
        // Special handling for "already recording" - stop existing and restart
        if (error.type === RecordingErrorType.ALREADY_RECORDING) {
            console.log("Recording already active, stopping existing recording and restarting...");
            try {
                await this.ScreenRecordingBridgeClient.stopRecording();
                // Wait a moment for cleanup
                await new Promise(resolve => setTimeout(resolve, 500));
                // Restart the recording process
                await this.startScreenRecordingAndProceed();
                return; // Exit early since we're handling this automatically
            } catch (stopError) {
                console.error("Failed to stop existing recording:", stopError);
                // Fall through to show error
            }
        }

        this.$timeout(() => {
            this.$scope.screenRecordingStatus.isStarting = false;
            this.$scope.screenRecordingStatus.hasError = true;
            this.$scope.screenRecordingStatus.errorType = error.type;
            
            switch (error.type) {
                case RecordingErrorType.PERMISSION_DENIED:
                    this.$scope.screenRecordingStatus.errorMessage = this.$translate.instant('MODULES.INSTRUCTIONS.SCREEN_RECORDING_PERMISSION_DENIED');
                    break;
                case RecordingErrorType.NOT_SUPPORTED:
                    this.$scope.screenRecordingStatus.errorMessage = this.$translate.instant('MODULES.INSTRUCTIONS.SCREEN_RECORDING_NOT_SUPPORTED');
                    break;
                case RecordingErrorType.ALREADY_RECORDING:
                    this.$scope.screenRecordingStatus.errorMessage = this.$translate.instant('MODULES.INSTRUCTIONS.SCREEN_RECORDING_RESTART_FAILED');
                    break;
                default:
                    this.$scope.screenRecordingStatus.errorMessage = this.$translate.instant('MODULES.INSTRUCTIONS.SCREEN_RECORDING_UNKNOWN_ERROR') + ': ' + error.message;
            }
        });
    }

    /**
     * Retries screen recording.
     */
    async retryScreenRecording() {
        if (this.$scope.screenRecordingStatus.retryCount >= this.$scope.screenRecordingStatus.maxRetries) {
            console.log("Maximum retry attempts reached");
            return;
        }
        
        // Prevent retry if already starting
        if (this.$scope.screenRecordingStatus.isStarting) {
            console.log("Screen recording is already being started, ignoring retry");
            return;
        }
        
        this.$scope.screenRecordingStatus.retryCount++;
        console.log(`Retrying screen recording (attempt ${this.$scope.screenRecordingStatus.retryCount})`);
        
        // Clear error state and set starting flag before retrying
        this.$scope.screenRecordingStatus.hasError = false;
        this.$scope.screenRecordingStatus.errorMessage = '';
        this.$scope.screenRecordingStatus.isStarting = true;
        
        try {
            await this.startScreenRecordingAndProceed();
        } catch (error) {
            console.warn("Error in retryScreenRecording:", error);
            if (window.Sentry) { window.Sentry.captureException(error, { tags: { context: 'screen_recording_retry' } }); }
            this.$timeout(() => {
                this.$scope.screenRecordingStatus.isStarting = false;
                this.$scope.screenRecordingStatus.hasError = true;
                this.$scope.screenRecordingStatus.errorMessage = this.$translate.instant('MODULES.INSTRUCTIONS.SCREEN_RECORDING_UNKNOWN_ERROR');
            });
        }
    }

    /**
     * Proceeds to the assessment after successful screen recording.
     */
    proceedToAssessment() {
        this.$timeout(() => {
            this.$scope.screenRecordingStatus.isStarting = false;
            this.$scope.screenRecordingStatus.hasError = false;
            
            // $logger.logEnter($scope, 'onStartButtonClick');
            this.$location.path(this.$scope.overviewUrl);

            const eMsg = this.$translate.instant('MODULES.INSTRUCTIONS.EVENTLOG_START');
            this.$eventLog.logEvent(eMsg, 'assessmentStart', '', '', {});

            // $logger.logExit($scope, 'onStartButtonClick');

            console.log("Instructions page start button: Starting ITS");
            this.InactivityTimeoutService.startWatch();
        });
    }

    /**
     * Handles the IAA setup button click action.
     */
    onIAASetupButtonClick() {
        this.$logger.info(this.$scope, "Loading IAASetup with url " + this.$scope.IAAUrl);
        this.$location.path(this.$scope.IAAUrl);
    }

    /**
     * Returns the type of the provided value.
     * 
     * @param {*} val - The value to determine the type of.
     * @returns {string} - The type of the value.
     */
    getTypeof(val) {
        return typeof val;
    }

    /**
     * Returns the BridgeRuntimeService instance.
     * 
     * @returns {Object} - The BridgeRuntimeService instance.
     */
    BridgeRuntimeService() {
        if (!this._BridgeRuntimeService) {
            this._BridgeRuntimeService = this.$injector.get('BridgeRuntimeService');
        }
        return this._BridgeRuntimeService;
    }
}

export default function registerInstructionsController(assessmentFrontendControllers) {
    assessmentFrontendControllers.controller('InstructionsController', ['$scope', '$rootScope', '$location', '$injector', '$routeParams', 'UserInfoService', 'TaskSelectionService', 'TaskFlowService', 'TaskInfoService', 'TaskDataService', '$bridges', 'IAAPreferencesService', '$translate', 'TaskCountdownService', 'InactivityTimeoutService', 'ScreenRecordingBridgeClient', '$timeout', InstructionsController]);
}
